// =============================================
// Layer 1 — Content Completeness Audit
// FREE (regex only), <10ms
// =============================================
// Sprawdza:
//   - czy treść nie jest urwana (markery: "[CONTINUE]", "...", "TBD", mid-paragraph cut)
//   - czy ma minimum struktury (1 H1 + co najmniej 2 H2)
//   - czy ma <script ld+json> z BlogPosting (publish injection success)
//   - word_count + h2/h3 count dla forensics
// =============================================

export interface CompletenessResult {
  status: "ok" | "warning" | "failed";
  word_count: number;
  h1_count: number;
  h2_count: number;
  h3_count: number;
  has_schema_ldjson: boolean;
  truncation_markers: string[];
  issues: string[];
}

const TRUNCATION_MARKERS = [
  "[CONTINUE]",
  "[TBD]",
  "more to come",
  "Wnioski w kolejnym akapicie",
  "ciąg dalszy nastąpi",
  "wkrótce uzupełnimy",
  "TODO:",
];

function countMatches(html: string, pattern: RegExp): number {
  const matches = html.match(pattern);
  return matches ? matches.length : 0;
}

function countWords(html: string): number {
  const text = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (!text) return 0;
  return text.split(/\s+/).length;
}

function endsTruncated(html: string): boolean {
  // strip trailing <script>/<noscript> + whitespace
  const stripped = html.replace(/<script[\s\S]*?<\/script>\s*$/i, "").trim();
  const lastChars = stripped.slice(-200).replace(/<[^>]*>/g, " ").trim();
  // Empty content
  if (!lastChars) return false;
  // Ostatni znak: kropka, ?, !, …, > (zamknięty tag) OR cyfra/litera bez interpunkcji = truncation
  const lastChar = lastChars.slice(-1);
  if (/[.!?…)\]"']/.test(lastChar)) return false;
  // Sprawdź czy ostatni "word" ma > 2 chars (krótkie domknięcie OK)
  const lastWord = lastChars.match(/\S+$/)?.[0] || "";
  return lastWord.length > 2;
}

export function auditCompleteness(finalHtml: string): CompletenessResult {
  const word_count = countWords(finalHtml);
  const h1_count = countMatches(finalHtml, /<h1\b/gi);
  const h2_count = countMatches(finalHtml, /<h2\b/gi);
  const h3_count = countMatches(finalHtml, /<h3\b/gi);
  // has_schema_ldjson zachowane w typ ale informacyjne — NIE wpływa na status.
  // Schema JSON-LD jest injektowana w publishQueueService TYLKO do payload WP, NIE
  // zapisywana z powrotem do thematic_node_content.final_html. Audit DB nigdy nie znajdzie.
  // Walidacja schema → Layer 5 wywołuje generateSchemasForNode runtime.
  const has_schema_ldjson = /<script[^>]*type=["']application\/ld\+json["'][^>]*>/i.test(finalHtml);

  const truncation_markers: string[] = [];
  for (const marker of TRUNCATION_MARKERS) {
    if (finalHtml.includes(marker)) truncation_markers.push(marker);
  }
  // Mid-sentence ending check — bardzo agresywny, daje false positives gdy HTML kończy się
  // np. </p></div> bez znaku interpunkcyjnego "tekstowego". Skipujemy chyba że są inne markers.
  // Real truncation pokażą TRUNCATION_MARKERS lub Layer 6 LLM.

  const issues: string[] = [];
  if (word_count < 500) issues.push(`Bardzo krótka treść: ${word_count} słów`);
  // H1 = WP renderuje go OSOBNO z post_title w theme template (poza .post-content).
  // post.content.rendered zwykle NIE ma <h1> — i to jest poprawne SEO (1 H1 per strona =
  // post_title w <h1> z theme, content tylko H2+H3). Wiele H1 w content = ostrzeżenie.
  if (h1_count > 1) issues.push(`Wiele H1 w treści (${h1_count}) — duplikat z post_title`);
  if (h2_count < 2) issues.push(`Mało H2 (${h2_count}) — typowo 3-6 dla SEO articles`);
  if (truncation_markers.length > 0) {
    issues.push(`Artykuł wygląda na urwany — wykryto markery: ${truncation_markers.join(", ")}`);
  }

  // Status:
  // failed = explicit truncation marker w treści
  // warning = mała liczba słów / mało H2 / wiele H1 (duplikat)
  // ok = inaczej
  let status: CompletenessResult["status"] = "ok";
  if (truncation_markers.length > 0) {
    status = "failed";
  } else if (word_count < 500 || h2_count < 2 || h1_count > 1) {
    status = "warning";
  }

  return {
    status,
    word_count,
    h1_count,
    h2_count,
    h3_count,
    has_schema_ldjson,
    truncation_markers,
    issues,
  };
}
